import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { track, EVENTS } from '@/lib/mixpanel';
import { useAuth } from '@/lib/auth-context';
import { IconArrowLeft, IconStar } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getTestimonialsByLanguage } from '@/content/testimonials';
import { getLanguagePath } from '@/lib/utils';
import { MetaHead } from "@/components/common/MetaHead";
import { usePromoCode } from '@/hooks/use-promo-code';
import { getStripeLookupKey, PlanId } from '@/lib/checkout';
import i18next from 'i18next';
import { usePlans } from '@/hooks/use-plans';
import { useTracking } from '@/hooks/use-tracking';
import { getMetaTrackingData, isBrowser } from '@/lib/meta';
import { getGA4TrackingData } from '@/lib/gtag';
import { IconCircleCheckFilled } from "@tabler/icons-react";
import Testimonials from '@/components/tabs/overview/Testimonials';
import AboutSection from '@/components/tabs/overview/AboutSection';
import Countdown from '@/components/common/Countdown';
import { loadStripe } from '@stripe/stripe-js';
import { CheckoutProvider } from '@stripe/react-stripe-js/checkout';
import { EmailInput } from '@/components/checkout/EmailInput';
import { PromoCodeInput } from '@/components/checkout/PromoCodeInput';
import { PaymentSection } from '@/components/checkout/PaymentSection';
import { PriceSummary } from '@/components/checkout/PriceSummary';
import { shouldShowCountdown } from '@/lib/countdown';
import { getCurrencyFromSymbol } from '@/lib/currencies';

// Get the API URL from environment variables
const API_URL = import.meta.env.VITE_WORKER_URL || '';

// Checkout mode
const checkoutMode = import.meta.env.VITE_CHECKOUT_MODE || 'login_optional';

export default function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromQuiz = searchParams.get('from') === 'quiz';
  const backPath = fromQuiz ? getLanguagePath('quiz/clarymind-app') : getLanguagePath('');
  const { user, loading: isAuthLoading } = useAuth();

  // @ts-ignore - Type instantiation depth limitation in TypeScript
  const { t, i18n } = useTranslation();

  // Initialize Stripe outside component to avoid recreating on every render
  // @ts-ignore -
  const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY, { locale: i18n.language || 'en' });

  // @ts-ignore -
  const { eventCode, eventData, referralCode, referralData, setEventCode, setReferralCode, clearReferralCode, validateReferralCode, getCountdownPromo, eventValidForPlan } = usePromoCode();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [testimonial, setTestimonial] = useState('');
  const [email, setEmail] = useState(user?.email || localStorage.getItem('checkoutEmail') || '');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [paymentMessage, setPaymentMessage] = useState<string | null>(null);

  const [referralCodeInput, setReferralCodeInput] = useState<string>(referralCode);
  const [promoError, setPromoError] = useState<string>('');
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [gradient, setGradient] = useState('from-blue-400 to-purple-500');
  const isRedirectingRef = useRef(false);
  const hasTrackedEmailRef = useRef<string | null>(null);
  const planId = (localStorage.getItem('pendingPurchasePlanId') as PlanId) || 'annual';

  // Scroll to top on mount
  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  }, []);

  // Set random testimonial and gradient
  useEffect(() => {
    const testimonials = getTestimonialsByLanguage(i18n.language);
    const randomTestimonialIndex = Math.floor(Math.random() * testimonials.length);
    const selected = testimonials[randomTestimonialIndex];
    if (selected) {
      setTestimonial(selected.content);
    }

    const gradients = [
      'from-blue-400 to-purple-500',
      'from-pink-500 to-orange-400',
      'from-green-400 to-cyan-500',
      'from-purple-500 to-pink-500',
      'from-yellow-400 to-orange-500',
      'from-blue-500 to-teal-400',
    ];
    const randomGradient = gradients[Math.floor(Math.random() * gradients.length)];
    if (randomGradient) {
      setGradient(randomGradient);
    }
  }, [i18n.language]);

  const TestimonialSection = () => {
    return (
      <div className="flex flex-col gap-3 max-w-sm pr-4">

        <div className="flex gap-1 mt-3">
          <IconStar className="w-3 h-3 fill-yellow-300 text-yellow-300" />
          <IconStar className="w-3 h-3 fill-yellow-300 text-yellow-300" />
          <IconStar className="w-3 h-3 fill-yellow-300 text-yellow-300" />
          <IconStar className="w-3 h-3 fill-yellow-300 text-yellow-300" />
          <IconStar className="w-3 h-3 fill-yellow-300 text-yellow-300" />
        </div>

        <p className="text-xs lg:text-sm leading-normal lg:leading-normal text-slate-500 lg:text-slate-500">
          {testimonial}
        </p>

        <div className="flex items-center gap-2">
          <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${gradient}`} />
          <p className="text-xs lg:text-sm leading-normal lg:leading-normal text-slate-500">
            {/* @ts-ignore - Type instantiation depth limitation */}
            {t('auth.appStoreReview')}
          </p>
        </div>
      </div>
    )
  }

  // Track page view
  useEffect(() => {
    if (!user?.id) {
      return;
    }

    // @ts-ignore - Type instantiation depth limitation
    track(EVENTS.VIEW_CHECKOUT_PAGE, {
      planId,
      userId: user?.id
    });
  }, [user?.id]);

  // Check if login is required and redirect if needed
  useEffect(() => {
    if (isAuthLoading || checkoutMode === 'login_optional') {
      return;
    }

    if (!user && checkoutMode === 'login_required') {
      if (isRedirectingRef.current) {
        return;
      }
      isRedirectingRef.current = true;
      localStorage.setItem('authRedirectTo', window.location.pathname);
      navigate(getLanguagePath('sign-in'));
    }
  }, [user, isAuthLoading, navigate]);

  // Create a promise that fetches the client secret (like Stripe sample code)
  const clientSecretPromise = useMemo(() => {
    // Don't create promise if auth is still loading or user needs to login
    // TODO: shouldn't be a reject or Error. Should wait for the auth is ready.
    if (isAuthLoading && checkoutMode === 'login_required') {
      return Promise.reject(new Error('Auth loading'));
    }

    if (!user && checkoutMode === 'login_required') {
      return Promise.reject(new Error('User must be signed in'));
    }

    if (planId !== 'quarterly' && planId !== 'annual') {
      return Promise.reject(new Error('Invalid plan ID'));
    }

    return fetch(`${API_URL}/api/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        lookup_key: getStripeLookupKey(planId),
        userId: user?.id,
        email: user?.email,
        provider: user?.app_metadata?.provider,
        return_path: getLanguagePath('confirm-account'),
        language: i18next.language || 'en',
        event_code: eventCode || undefined,
        referral_code: referralCode || undefined,
        ui_mode: 'custom',
        meta_tracking: {
          ...getMetaTrackingData(),
          event_id: (isBrowser() ? window.sessionStorage.getItem('initiate_checkout_event_id') : null) || getMetaTrackingData().event_id,
          email: localStorage.getItem('checkoutEmail') || undefined,
        },
        ga4_tracking: getGA4TrackingData(),
      }),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().then((errorData) => {
            throw new Error(errorData.message || 'Failed to create checkout session');
          });
        }
        return res.json();
      })
      .then((data) => {
        setSessionId(data.session_id);
        return data.client_secret;
      });
  }, [user, isAuthLoading, planId, eventCode, referralCode]);

  // Get plans data for promo code section
  const { plans } = usePlans();
  const currentPlan = plans.find(p => p.id === planId);
  const planPricing = currentPlan?.pricing as any;

  useEffect(() => {
    setReferralCodeInput(referralCode);
  }, [referralCode]);

  // When stored referral loads and is invalid, keep code (send to backend) and don't show error in UI
  useEffect(() => {
    if (referralCode && referralData && !referralData.valid) {
      setReferralCodeInput('');
      // setPromoError(t('checkout.promoCodeInvalid'));
    }
  }, [referralCode, referralData, t]);

  const handleRemoveReferralCode = () => {
    clearReferralCode();
    setReferralCodeInput('');
    setPromoError('');
  };

  const handleReferralCodeChange = (value: string) => {
    setReferralCodeInput(value.toUpperCase());
    setPromoError('');
  };

  const handleValidateReferralCode = async () => {
    const upperValue = referralCodeInput.toUpperCase().trim();
    if (!upperValue) {
      clearReferralCode();
      setPromoError('');
      return;
    }
    try {
      const data = await validateReferralCode(upperValue);

      // if referral is invalid, keep the code (to send to backend) but clear the input and show error
      if (data.valid) {
        if (data.type === 'event') {
          setEventCode(upperValue);
          setReferralCodeInput('');
          setPromoError('');
        } else {
          setReferralCode(upperValue, data);
          setReferralCodeInput(upperValue);
          setPromoError('');
        }
      } else {
        setReferralCode(upperValue, data);
        setReferralCodeInput('');
        // @ts-ignore - Type instantiation depth limitation
        setPromoError(t('checkout.promoCodeInvalid'));
      }
    } catch (err) {
      console.error('Error validating referral code:', err);
      setReferralCode(upperValue);
      setReferralCodeInput('');
      setPromoError(t('checkout.promoCodeInvalid'));
    }
  };

  const handleReferralCodeBlur = () => handleValidateReferralCode();
  const handleReferralCodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleValidateReferralCode();
    }
  };

  const handleEmailValidated = async (validatedEmail: string) => {
    // Track checkout when email is validated (only once per email)
    if (currentPlan && hasTrackedEmailRef.current !== validatedEmail) {
      // Reuse existing event_id from sessionStorage for deduplication
      // This ensures the same checkout intent is tracked consistently
      hasTrackedEmailRef.current = validatedEmail;

      // Send enriched tracking event with email
      if (currentPlan) {
        try {
          const currency = getCurrencyFromSymbol(currentPlan.pricing.currency);
          const lookupKey = getStripeLookupKey(currentPlan.id as PlanId);

          await fetch(`${API_URL}/api/track-email-update`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: validatedEmail,
              value: currentPlan.pricing.current,
              currency: currency,
              item_id: lookupKey,
              item_name: currentPlan.id,
              meta_tracking: {
                ...getMetaTrackingData(),
                event_id: (isBrowser() ? window.sessionStorage.getItem('initiate_checkout_event_id') : null) || getMetaTrackingData().event_id,
                email: localStorage.getItem('checkoutEmail') || undefined,
              },
              ga4_tracking: getGA4TrackingData(),
              ...(eventCode && { event_code: eventCode }),
              ...(referralCode && { referral_code: referralCode }),
              ...(user?.id && { external_id: user.id }),
            }),
          });
        } catch (err) {
          // Silently fail - tracking is best effort
          console.error('Failed to send email tracking event:', err);
        }
      }
    }
  };

  const handleCheckoutComplete = () => {
    localStorage.removeItem('pendingPurchasePlanId');
    localStorage.removeItem('authRedirectTo');
    // @ts-ignore - Type instantiation depth limitation
    navigate(getLanguagePath(`success?session_id=${sessionId}`));
  };

  // @ts-ignore - Type instantiation depth limitation
  const features = t('productIntro.features', { returnObjects: true }) as string[];
  const currentLookupKey = getStripeLookupKey(planId);
  const countdownPromo = useMemo(() => getCountdownPromo(currentLookupKey), [getCountdownPromo, currentLookupKey]);
  const countdownTargetDate = useMemo(
    () => (countdownPromo?.expires_at ? new Date(countdownPromo.expires_at * 1000) : null),
    [countdownPromo?.expires_at]
  );
  const eventAppliesToPlan = useMemo(
    () => eventValidForPlan(currentLookupKey),
    [eventValidForPlan, currentLookupKey]
  );

  const InfoSection = ({ className }: { className?: string }) => {
    const showCountdown = shouldShowCountdown(countdownTargetDate);

    return <div className={className}>
      {showCountdown && countdownTargetDate && (
        <div className="px-8 flex flex-col items-center justify-center">
          <Countdown
            targetDate={countdownTargetDate}
            className="mx-auto"
            textColor="text-slate-700"
            numberColor="text-red-600"
            promoCode={countdownPromo?.code}
          />
          <div className="h-px w-full bg-slate-100 my-6"></div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4 pb-12 px-8">
        {features.map((item, index) => (
          <div key={index} className={`flex items-center gap-3`}>
            <IconCircleCheckFilled className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span>{item}</span>
          </div>
        ))}
      </div>

      <Testimonials />
      <AboutSection />
    </div>
  }

  const appearance = {
    theme: 'stripe' as const,
    variables: {
      colorPrimary: '#0ea5e9', // sky-500
      colorBackground: '#ffffff',
      colorText: '#1e293b', // slate-800
      colorDanger: '#ef4444',
      fontFamily: 'system-ui, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
    },
  };

  return (
    <div className={`min-h-screen grid lg:grid-cols-12 overflow-hidden lg:fixed lg:w-full lg:h-screen lg:inset-0`}>
      <MetaHead
        title={t('meta.checkout.title')}
        description={t('meta.checkout.description', 'Complete your Clarymind subscription purchase')}
      />

      {/* Left Section - Desktop */}
      <div className={`hidden lg:flex flex-col lg:col-span-5 bg-slate-950 pl-24 pr-0 h-screen`}>
        {/* <div className="p-6 lg:p-10">
          <Link to={getLanguagePath('')}>
            <img
              src="/brands/clarymind-logo_logotype-app-transparent-dark-outline.svg"
              alt="Clarymind"
              className="h-12 w-auto"
            />
          </Link>
        </div> */}
        {/* Navigation and Function Bar */}
        <div className="w-full max-w-xl mx-auto flex flex-row justify-start items-start space-x-2 text-center px-6 lg:px-10 pb-8 lg:pb-8 lg:pt-14">

          {/* Back Button */}
          <button
            onClick={() => navigate(backPath)}
            className="px-0 py-0 h-8 text-slate-500 rounded-xl text-sm hover:text-slate-100 flex items-center gap-1 group transition-all duration-300 shrink-0"
          >
            <IconArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-all duration-300" />
            {t('checkout.back')}
          </button>
        </div>
        <div className="px-6 lg:px-10">
          <PriceSummary planId={planId} />
        </div>
        <div className="p-6 lg:px-10 lg:pb-8 mt-auto mb-8">
          <TestimonialSection />
        </div>
      </div>

      {/* Main Content Section */}
      <div className={`relative lg:col-span-7 flex flex-col items-center justify-start lg:h-screen lg:overflow-auto min-w-0 px-0 lg:px-6 pt-0 pb-6 lg:pt-14 lg:pb-8 lg:pl-0 lg:pr-16`}>

        {/* Fixed countdown bar at top - Desktop */}
        {shouldShowCountdown(countdownTargetDate) && countdownTargetDate && (
          <div className="fixed lg:absolute w-full top-0 left-0 right-0 z-50 bg-slate-950 lg:bg-slate-200 border-b border-b-slate-800 lg:border-b-slate-300 h-9 flex items-center lg:pr-16">
            <div className="mx-auto">
              <Countdown
                targetDate={countdownTargetDate}
                className="mx-auto"
                textColor="text-slate-50 lg:text-slate-800"
                numberColor="text-red-400 lg:text-red-600"
                promoCode={countdownPromo?.code}
              />
            </div>
          </div>
        )}

        {/* Price Summary - Mobile */}
        <div className={`w-full max-w-4xl mx-auto lg:hidden bg-slate-950 pb-5 px-4 lg:px-0 ${shouldShowCountdown(countdownTargetDate) && countdownTargetDate ? "pt-12" : "pt-2"}`}>

          {/* Navigation and Function Bar */}
          <div className="w-full max-w-xl mx-auto flex flex-row justify-start items-start space-x-2 text-center px-2.5 pt-2 pb-4">
            <button
              onClick={() => navigate(getLanguagePath(''))}
              className="px-0 py-0 h-8 text-slate-400 rounded-xl text-sm hover:text-slate-500 flex items-center gap-1 group transition-all duration-300 shrink-0"
            >
              <IconArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-all duration-300" />
              {t('checkout.back')}
            </button>
          </div>

          <PriceSummary planId={planId} />
        </div>

        <CheckoutProvider
          key={`checkout-${planId}-${eventCode ?? ''}-${referralCode ?? ''}-${eventData?.valid ?? false}-${referralData?.valid ?? false}`}
          stripe={stripePromise}
          options={{
            clientSecret: clientSecretPromise,
            elementsOptions: { appearance },
          }}
        >
          <div className="w-full max-w-xl space-y-4 pt-6 px-6">

            {/* Email and Promo Code - Always visible */}
            {!user?.email && (
              <EmailInput
                email={email}
                setEmail={setEmail}
                error={emailError}
                setError={setEmailError}
                onEmailValidated={handleEmailValidated}
              />
            )}

            <PromoCodeInput
              eventCode={eventCode}
              eventData={eventData}
              eventAppliesToPlan={eventAppliesToPlan}
              referralCode={referralCode}
              referralValid={!!referralData?.valid}
              referralCodeInput={referralCodeInput}
              promoError={promoError}
              showPromoInput={showPromoInput}
              onExpandPromoInput={() => setShowPromoInput(true)}
              planPricing={planPricing}
              currentPlan={currentPlan}
              onReferralCodeChange={handleReferralCodeChange}
              onReferralCodeBlur={handleReferralCodeBlur}
              onReferralCodeKeyDown={handleReferralCodeKeyDown}
              onRemoveReferralCode={handleRemoveReferralCode}
            />

            {/* Payment Section - Handles loading/error states internally */}
            <PaymentSection
              onComplete={handleCheckoutComplete}
              email={email}
              setEmailError={setEmailError}
              setMessage={setPaymentMessage}
              planPricing={planPricing}
              currentPlan={currentPlan}
              sessionId={sessionId}
            />
            {/* {paymentMessage && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {paymentMessage}
              </div>
            )} */}
          </div>
        </CheckoutProvider>
        <InfoSection className="hidden md:block w-full mt-12 max-w-3xl" />
      </div>

      {/* Info (Mobile Only) */}
      <InfoSection className="md:hidden" />
    </div>
  )
}
